import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { validateSsn } from "../../shared/utils/ssnValidation";
import styles from "./WorkflowSsnScreen.module.scss";

function formatSSN(digits: string): string {
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

interface Props {
  onSubmit: (ssn: string) => void;
  onNoSsn: () => void;
  isSubmitting: boolean;
}

export function WorkflowSsnScreen({ onSubmit, onNoSsn, isSubmitting }: Props) {
  const { t } = useTranslation();
  const [digits, setDigits] = useState("");
  const [shown, setShown] = useState(false);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 9);
    setDigits(raw);
  }, []);

  // Only validate a complete 9-digit value — partial entry isn't an error.
  const validationError = digits.length === 9 ? validateSsn(digits) : null;
  const isValid = digits.length === 9 && validationError === null;

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    onSubmit(digits);
  }, [digits, isValid, isSubmitting, onSubmit]);

  const displayValue = shown ? formatSSN(digits) : digits;

  return (
    <div className={styles.ssnCard}>
      <h1 className={styles.ssnTitle}>{t("ssn.screen.title")}</h1>
      <p className={styles.ssnSubtitle}>{t("ssn.screen.subtitle")}</p>

      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="ssn-input">
            <span className={styles.labelText}>{t("ssn.screen.label")}</span>
            <span className={styles.labelExample}>{t("ssn.screen.labelExample")}</span>
          </label>
          <div className={`${styles.inputWrapper} ${validationError ? styles.hasError : ""}`}>
            <input
              id="ssn-input"
              type={shown ? "text" : "password"}
              inputMode="numeric"
              className={styles.ssnInput}
              placeholder=""
              value={displayValue}
              onChange={handleChange}
              maxLength={shown ? 11 : 9}
              autoComplete="off"
              disabled={isSubmitting}
              aria-invalid={validationError !== null}
              aria-describedby={validationError ? "ssn-error" : undefined}
            />
            <button
              type="button"
              className={styles.showHideButton}
              onClick={() => setShown((v) => !v)}
              aria-controls="ssn-input"
              aria-pressed={shown}
              aria-label={shown ? t("ssn.screen.hideAriaLabel") : t("ssn.screen.showAriaLabel")}
            >
              {shown ? t("ssn.screen.hide") : t("ssn.screen.show")}
            </button>
          </div>
          {validationError && (
            <p id="ssn-error" className={styles.fieldError} role="alert">
              {t(`ssn.screen.error.${validationError}`)}
            </p>
          )}
        </div>

        <button
          type="submit"
          className={styles.continueButton}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? t("ssn.screen.submittingButton") : t("ssn.screen.continueButton")}
        </button>
      </form>

      <div className={styles.noSsnWrapper}>
        <button type="button" className={styles.noSsnLink} onClick={onNoSsn}>
          {t("ssn.noSsn")}
        </button>
      </div>
    </div>
  );
}
